import { useState } from "react";
import { toast } from "sonner";
import { Sparkle, ArrowDown, WarningCircle } from "@phosphor-icons/react";
import {
  US_STATES,
  SUBJECTS,
  GRADES,
  buildRubric,
} from "@/lib/rubricTemplates";
import PrintableRubric from "@/components/PrintableRubric";

const labelClass = "block text-sm font-bold text-[#0A0A0A] mb-1.5";
const inputClass =
  "w-full h-11 bg-white border border-[#CBD5E1] rounded-lg px-3.5 text-sm font-medium text-[#0A0A0A] focus:outline-none focus:border-[#0A0A0A] focus:ring-2 focus:ring-[#A8B5A2] transition-all";
const inputErrorClass =
  "w-full h-11 bg-white border border-[#DC2626] rounded-lg px-3.5 text-sm font-medium text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#DC2626]/40 transition-all";
const selectClass =
  "w-full h-11 bg-white border border-[#CBD5E1] rounded-lg px-3 text-sm font-medium text-[#0A0A0A] focus:outline-none focus:border-[#0A0A0A] focus:ring-2 focus:ring-[#A8B5A2] transition-all appearance-none cursor-pointer pr-8";
const selectErrorClass =
  "w-full h-11 bg-white border border-[#DC2626] rounded-lg px-3 text-sm font-medium text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#DC2626]/40 transition-all appearance-none cursor-pointer pr-8";

function FieldError({ id, message }) {
  if (!message) return null;
  return (
    <p
      id={id}
      role="alert"
      data-testid={id}
      className="mt-1.5 flex items-center gap-1 text-xs font-semibold text-[#DC2626]"
    >
      <WarningCircle size={14} weight="fill" />
      {message}
    </p>
  );
}

function validate(form) {
  const errors = {};
  if (!form.subject) errors.subject = "Please choose a subject.";
  if (!form.grade) errors.grade = "Please choose a grade level.";
  if (!form.topic.trim()) errors.topic = "Please enter the assignment topic.";

  const points = parseInt(form.totalPoints, 10);
  if (!Number.isFinite(points) || points <= 0 || !/^\d+$/.test(String(form.totalPoints).trim())) {
    errors.totalPoints = "Total points must be a positive whole number.";
  } else if (points > 1000) {
    errors.totalPoints = "Total points must be 1000 or less.";
  }
  return errors;
}

export default function RubricGenerator() {
  const [form, setForm] = useState({
    state: "",
    subject: "",
    grade: "",
    topic: "",
    totalPoints: 20,
  });
  const [errors, setErrors] = useState({});
  const [rubric, setRubric] = useState(null);

  const update = (key) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleGenerate = (e) => {
    e?.preventDefault();
    const v = validate(form);
    if (Object.keys(v).length > 0) {
      setErrors(v);
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setErrors({});
    // 100% client-side — no network call.
    const r = buildRubric(form);
    setRubric(r);
    toast.success("Rubric ready — scroll down to view, copy, or print.");
    setTimeout(() => {
      document
        .getElementById("rubric-result")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  const handleReset = () => {
    setRubric(null);
    setErrors({});
    setForm({ state: "", subject: "", grade: "", topic: "", totalPoints: 20 });
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-sm p-6 md:p-10">
      <form onSubmit={handleGenerate} noValidate className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label htmlFor="rg-state" className={labelClass}>
            State <span className="text-[#64748B] font-medium">(optional)</span>
          </label>
          <div className="relative">
            <select
              id="rg-state"
              data-testid="state-select"
              value={form.state}
              onChange={update("state")}
              className={selectClass}
            >
              <option value="">— No state —</option>
              {US_STATES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] text-xs">▼</span>
          </div>
        </div>

        <div>
          <label htmlFor="rg-subject" className={labelClass}>
            Subject <span className="text-[#DC2626]">*</span>
          </label>
          <div className="relative">
            <select
              id="rg-subject"
              data-testid="subject-select"
              value={form.subject}
              onChange={update("subject")}
              aria-invalid={!!errors.subject}
              aria-describedby={errors.subject ? "subject-error" : undefined}
              className={errors.subject ? selectErrorClass : selectClass}
            >
              <option value="">Select a subject</option>
              {SUBJECTS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] text-xs">▼</span>
          </div>
          <FieldError id="subject-error" message={errors.subject} />
        </div>

        <div>
          <label htmlFor="rg-grade" className={labelClass}>
            Grade Level <span className="text-[#DC2626]">*</span>
          </label>
          <div className="relative">
            <select
              id="rg-grade"
              data-testid="grade-select"
              value={form.grade}
              onChange={update("grade")}
              aria-invalid={!!errors.grade}
              aria-describedby={errors.grade ? "grade-error" : undefined}
              className={errors.grade ? selectErrorClass : selectClass}
            >
              <option value="">Select a grade</option>
              {GRADES.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] text-xs">▼</span>
          </div>
          <FieldError id="grade-error" message={errors.grade} />
        </div>

        <div>
          <label htmlFor="rg-points" className={labelClass}>
            Total Points <span className="text-[#DC2626]">*</span>
          </label>
          <input
            id="rg-points"
            data-testid="points-input"
            type="number"
            inputMode="numeric"
            min="1"
            max="1000"
            step="1"
            value={form.totalPoints}
            onChange={update("totalPoints")}
            aria-invalid={!!errors.totalPoints}
            aria-describedby={errors.totalPoints ? "points-error" : undefined}
            className={errors.totalPoints ? inputErrorClass : inputClass}
          />
          <FieldError id="points-error" message={errors.totalPoints} />
        </div>

        <div className="md:col-span-2">
          <label htmlFor="rg-topic" className={labelClass}>
            Assignment Topic <span className="text-[#DC2626]">*</span>
          </label>
          <input
            id="rg-topic"
            data-testid="topic-input"
            type="text"
            value={form.topic}
            onChange={update("topic")}
            placeholder="Example: Black History project, science lab report, persuasive essay"
            maxLength={120}
            aria-invalid={!!errors.topic}
            aria-describedby={errors.topic ? "topic-error" : undefined}
            className={errors.topic ? inputErrorClass : inputClass}
          />
          <FieldError id="topic-error" message={errors.topic} />
        </div>

        <div className="md:col-span-2 flex flex-col sm:flex-row gap-3 pt-1">
          <button
            type="submit"
            data-testid="generate-btn"
            className="inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 font-bold text-base shadow-sm transition-all bg-[#0A0A0A] text-white hover:bg-[#1E1E1E] hover:shadow"
          >
            <Sparkle size={20} weight="fill" />
            Generate Rubric
          </button>
          {rubric ? (
            <button
              type="button"
              onClick={handleReset}
              data-testid="reset-btn"
              className="inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 font-bold text-base bg-white border border-[#CBD5E1] text-[#0A0A0A] hover:bg-[#F8FAFC] transition-colors"
            >
              Start Over
            </button>
          ) : null}
        </div>
      </form>

      {rubric ? (
        <div className="mt-10" id="rubric-result">
          <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-[#64748B] mb-4">
            <ArrowDown size={16} weight="bold" />
            Your rubric
          </div>
          <PrintableRubric rubric={rubric} />
        </div>
      ) : null}
    </div>
  );
}
